// UpgradeModal Component
// Antigravity-style glassmorphism modal with floating pricing cards

import React, { useEffect, useState } from "react";
import { X, Sparkles, Check, Zap, Crown } from "lucide-react";
import { generateCheckoutUrl } from "../services/subscriptionService";
import { supabase } from "../services/supabase";

interface UpgradeModalProps {
    isOpen: boolean;
    onClose: () => void;
    feature?: string;
}

const FEATURES = [
    "Unlimited AI Study Tips",
    "Unlimited Smart Task breakdowns",
    "Full Analytics history",
    "Create & join unlimited groups",
    "AI-powered weekly scheduling",
    "Pet skins & evolution",
    "Competitive leaderboards",
];

export function UpgradeModal({ isOpen, onClose, feature }: UpgradeModalProps) {
    const [selectedPlan, setSelectedPlan] = useState<"monthly" | "annual">("annual");
    const [userId, setUserId] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [userLoading, setUserLoading] = useState(true);

    useEffect(() => {
        if (isOpen) {
            setUserLoading(true);
            supabase.auth.getUser().then(({ data, error }) => {
                if (error) {
                    console.error("Failed to get user:", error);
                }
                setUserId(data.user?.id || null);
                setUserLoading(false);
            });
        }
    }, [isOpen]);

    // Handle escape key
    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        if (isOpen) {
            document.addEventListener("keydown", handleEscape);
            document.body.style.overflow = "hidden";
        }
        return () => {
            document.removeEventListener("keydown", handleEscape);
            document.body.style.overflow = "";
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const handleUpgrade = () => {
        if (!userId) return;
        setIsLoading(true);
        const checkoutUrl = generateCheckoutUrl(userId, selectedPlan);
        // Open Lemon Squeezy checkout in new tab
        window.open(checkoutUrl, "_blank");
        setIsLoading(false);
        onClose();
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="upgrade-modal-title"
        >
            {/* Backdrop with subtle blur */}
            <div
                className="absolute inset-0 bg-black/40 backdrop-blur-sm animate-fade-in"
                onClick={onClose}
            />

            {/* Modal - Antigravity floating style */}
            <div className="relative w-full max-w-lg bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 dark:border-gray-700/50 overflow-hidden animate-float-up">

                {/* Header gradient accent */}
                <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-amber-400 via-orange-500 to-amber-400" />

                {/* Close button */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors z-10"
                    aria-label="Close"
                >
                    <X className="w-5 h-5 text-gray-500" />
                </button>

                <div className="p-6 pt-8">
                    {/* Header */}
                    <div className="text-center mb-6">
                        <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 shadow-lg mb-4">
                            <Crown className="w-7 h-7 text-white" />
                        </div>
                        <h2 id="upgrade-modal-title" className="text-2xl font-bold text-gray-900 dark:text-white">
                            Upgrade to Premium
                        </h2>
                        <p className="text-gray-500 dark:text-gray-400 mt-1">
                            Remove limits. Study without friction.
                        </p>
                    </div>

                    {/* Pricing Cards - Floating style */}
                    <div className="flex gap-3 mb-6">
                        {/* Monthly */}
                        <button
                            onClick={() => setSelectedPlan("monthly")}
                            className={`flex-1 p-4 rounded-2xl border-2 transition-all duration-200 ${selectedPlan === "monthly"
                                    ? "border-amber-500 bg-amber-50 dark:bg-amber-900/20 shadow-lg scale-[1.02]"
                                    : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
                                }`}
                        >
                            <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">Monthly</div>
                            <div className="text-2xl font-bold text-gray-900 dark:text-white">$4.99</div>
                            <div className="text-xs text-gray-400">/month</div>
                        </button>

                        {/* Annual - Highlighted */}
                        <button
                            onClick={() => setSelectedPlan("annual")}
                            className={`flex-1 p-4 rounded-2xl border-2 transition-all duration-200 relative ${selectedPlan === "annual"
                                    ? "border-amber-500 bg-amber-50 dark:bg-amber-900/20 shadow-lg scale-[1.02]"
                                    : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
                                }`}
                        >
                            {/* Best value badge */}
                            <div className="absolute -top-2 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-gradient-to-r from-amber-400 to-orange-500 rounded-full text-[10px] font-bold text-white uppercase tracking-wide">
                                Save 35%
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">Annual</div>
                            <div className="text-2xl font-bold text-gray-900 dark:text-white">$39</div>
                            <div className="text-xs text-gray-400">/year</div>
                        </button>
                    </div>

                    {/* Features list - Compact */}
                    <div className="bg-gray-50 dark:bg-gray-800/50 rounded-2xl p-4 mb-6">
                        <div className="grid grid-cols-2 gap-2">
                            {FEATURES.map((f, i) => (
                                <div key={i} className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                                    <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                                    <span className="truncate">{f}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* CTA Button - Calm, non-aggressive */}
                    <button
                        onClick={handleUpgrade}
                        disabled={isLoading || userLoading || !userId}
                        className="w-full py-4 px-6 bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-500 hover:to-orange-600 text-white font-semibold rounded-2xl shadow-lg hover:shadow-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 group"
                    >
                        {userLoading ? (
                            <span>Loading...</span>
                        ) : !userId ? (
                            <span>Please log in first</span>
                        ) : (
                            <>
                                <Sparkles className="w-5 h-5 group-hover:animate-pulse" />
                                <span>Remove limits</span>
                                <Zap className="w-4 h-4 opacity-70" />
                            </>
                        )}
                    </button>

                    {/* Fine print */}
                    <p className="text-center text-xs text-gray-400 mt-4">
                        Cancel anytime. Secure checkout via Lemon Squeezy.
                    </p>
                </div>
            </div>

            <style>{`
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes float-up {
          from { 
            opacity: 0; 
            transform: translateY(20px) scale(0.95); 
          }
          to { 
            opacity: 1; 
            transform: translateY(0) scale(1); 
          }
        }
        .animate-fade-in {
          animation: fade-in 200ms ease-out;
        }
        .animate-float-up {
          animation: float-up 250ms ease-out;
        }
      `}</style>
        </div>
    );
}

// Global modal provider component
export function UpgradeModalProvider({ children }: { children: React.ReactNode }) {
    const [isOpen, setIsOpen] = useState(false);
    const [feature, setFeature] = useState<string | undefined>();

    useEffect(() => {
        const handleOpen = (e: CustomEvent<{ feature?: string }>) => {
            setFeature(e.detail?.feature);
            setIsOpen(true);
        };

        window.addEventListener("triyo:open-upgrade-modal", handleOpen as EventListener);
        return () => {
            window.removeEventListener("triyo:open-upgrade-modal", handleOpen as EventListener);
        };
    }, []);

    return (
        <>
            {children}
            <UpgradeModal
                isOpen={isOpen}
                onClose={() => setIsOpen(false)}
                feature={feature}
            />
        </>
    );
}

export default UpgradeModal;
